export const REMOTE_SENSING_PRODUCTS = {
  SENTINEL: 'sentinel',
  PLANET_SCOPE_HD: 'planetscope_hd',
}

export const PLANET_PROVIDER_PRODUCT = 'planetscope_psscene_sr_8b'

export const REMOTE_SENSING_PRODUCT_ALIASES = {
  hd: REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD,
  planet_scope_hd: REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD,
  planetscope_psscene_sr_8b: REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD,
  planetscope: REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD,
  sentinel: REMOTE_SENSING_PRODUCTS.SENTINEL,
}

export const REMOTE_SENSING_LAYERS = {
  VISIBLE: 'visible',
  NDVI: 'ndvi',
  EVI: 'evi',
  NDRE: 'ndre',
}

export const PRODUCT_LAYER_OPTIONS = {
  [REMOTE_SENSING_PRODUCTS.SENTINEL]: [
    REMOTE_SENSING_LAYERS.VISIBLE,
    REMOTE_SENSING_LAYERS.NDVI,
    REMOTE_SENSING_LAYERS.EVI,
  ],
  [REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD]: [
    REMOTE_SENSING_LAYERS.VISIBLE,
    REMOTE_SENSING_LAYERS.NDVI,
    REMOTE_SENSING_LAYERS.EVI,
    REMOTE_SENSING_LAYERS.NDRE,
  ],
}

const IMAGE_TYPE_BY_SOURCE = {
  [`${REMOTE_SENSING_PRODUCTS.SENTINEL}:${REMOTE_SENSING_LAYERS.VISIBLE}`]: 'rgb',
  [`${REMOTE_SENSING_PRODUCTS.SENTINEL}:${REMOTE_SENSING_LAYERS.NDVI}`]: 'ndvi',
  [`${REMOTE_SENSING_PRODUCTS.SENTINEL}:${REMOTE_SENSING_LAYERS.EVI}`]: 'evi',
  [`${REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD}:${REMOTE_SENSING_LAYERS.VISIBLE}`]: 'rgb_hd',
  [`${REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD}:${REMOTE_SENSING_LAYERS.NDVI}`]: 'ndvi_hd',
  [`${REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD}:${REMOTE_SENSING_LAYERS.EVI}`]: 'evi_hd',
  [`${REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD}:${REMOTE_SENSING_LAYERS.NDRE}`]: 'ndre_hd',
}

const SOURCE_BY_IMAGE_TYPE = Object.entries(IMAGE_TYPE_BY_SOURCE).reduce(
  (result, [key, imageType]) => {
    const [product, layer] = key.split(':')
    result[imageType] = {product, layer}
    return result
  },
  {}
)

const normalizeProduct = (value, quality) => {
  const normalizedValue = REMOTE_SENSING_PRODUCT_ALIASES[value] || value
  if (normalizedValue === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD || quality === 'hd') {
    return REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD
  }

  return REMOTE_SENSING_PRODUCTS.SENTINEL
}

const normalizeLayer = (value) => {
  if (value === 'rgb') {
    return REMOTE_SENSING_LAYERS.VISIBLE
  }

  return Object.values(REMOTE_SENSING_LAYERS).includes(value)
    ? value
    : REMOTE_SENSING_LAYERS.VISIBLE
}

export const isPlanetHdProduct = (product) =>
  product === REMOTE_SENSING_PRODUCTS.PLANET_SCOPE_HD

export const getProductLayerOptions = (product) =>
  PRODUCT_LAYER_OPTIONS[product] || []

export const getImageTypeForSource = ({product, layer}) =>
  IMAGE_TYPE_BY_SOURCE[`${product}:${layer}`] || null

export const normalizeRemoteSensingSource = (value, fallbackImageType = 'rgb') => {
  const legacyImageType = typeof value === 'string'
    ? value
    : value?.imageType || value?.image_type || fallbackImageType
  const legacySource = SOURCE_BY_IMAGE_TYPE[legacyImageType]
  const product = normalizeProduct(
    value?.product || value?.source?.product || legacySource?.product,
    value?.quality || value?.source?.quality
  )
  const requestedLayer = normalizeLayer(
    value?.layer || value?.source?.layer || legacySource?.layer
  )
  const supportedLayers = getProductLayerOptions(product)
  const layer = supportedLayers.includes(requestedLayer)
    ? requestedLayer
    : supportedLayers[0]
  const imageType = getImageTypeForSource({product, layer})

  return {
    product,
    provider: isPlanetHdProduct(product) ? 'planet' : 'sentinel',
    quality: isPlanetHdProduct(product) ? 'hd' : 'standard',
    layer,
    imageType,
  }
}

export const buildRemoteSensingSourcePayload = (source) => {
  const normalized = normalizeRemoteSensingSource(source)
  const apiLayer = normalized.layer === REMOTE_SENSING_LAYERS.VISIBLE
    ? 'rgb'
    : normalized.layer

  return {
    product: normalized.product,
    ...(isPlanetHdProduct(normalized.product)
      ? {provider_product: PLANET_PROVIDER_PRODUCT}
      : {}),
    provider: normalized.provider,
    quality: normalized.quality,
    layer: apiLayer,
    display_layer: normalized.layer,
    image_type: normalized.imageType,
  }
}

export const normalizePersistedSource = (record = {}) =>
  normalizeRemoteSensingSource(
    record.source || record.remote_sensing_source || record.image_type || record.imageType,
    record.image_type || record.imageType || 'rgb'
  )
